<script>
// Module scope — persists across SPA route unmount/remount. FormAssembly's "publish"
// loader only renders on a full page load, so once the form is rendered we keep the
// node alive here and re-attach it on the next visit instead of re-running the loader
// (which would re-inject its jQuery/wForms/stylesheets/analytics every time).
//
// Keyed by formId: the site now embeds two distinct inline forms (the /contact form and
// the Academy programs form 4751235), so a single shared slot would re-attach the wrong
// cached form when navigating between them. Each formId caches its own detached fragment;
// the loader's global `window.loadFormAssemblyFormHeadAndBodyContents` still renders into
// this instance's own `data-qp-target-id`, so per-formId caching keeps them independent.
const formCache = new Map()
</script>

<script setup>
import { onMounted, onBeforeUnmount, ref } from 'vue'

const props = defineProps({
  formId:  { type: String, required: true },
  baseUrl: { type: String, default: 'https://www.tfaforms.com' },
})

// Stable element the FormAssembly loader renders the form into (its data-qp-target-id).
// Living inside Contact.vue's `.contact-form` wrapper is what lets that view's `:deep()`
// styles reach the injected form markup.
const targetId = `fa-form-${props.formId}`
const scriptId = `fa-inline-${props.formId}`
const target = ref(null)

// FA's loader injects the form's <head> content — including a per-form theme stylesheet —
// and never takes it back out. Each form in this account uses a *different* theme (Contact
// and Home load theme-68007, the Academy programs form theme-97829), and those themes
// disagree, so left to accumulate they stack: visiting Home and then routing to an Academy
// page left both live and pulled that form's FA-`required` fields (First/Last/Email) to
// theme-68007's 2px grey treatment while its un-classed Phone/Postcode kept the site's 8px.
// So: snapshot the theme links present before we inject, and drop whatever this form added
// when the route unmounts. The mixin's field rules are !important and no longer depend on
// this, but the theme also reaches labels, the privacy note and the container's inherited
// font — and letting third-party stylesheets pile up per route is a trap for the next form.
const THEME_LINK = 'link[href*="/uploads/themes/theme-"]'
const preexistingThemes = new Set()

const dropInjectedThemes = () => {
  document.head.querySelectorAll(THEME_LINK).forEach((link) => {
    if (!preexistingThemes.has(link)) link.remove()
  })
  preexistingThemes.clear()
}

// Belt-and-braces: if FormAssembly's loader ever injects its wrapper as a direct child
// of <body> (its fallback when it can't resolve data-qp-target-id at run time — this is
// what rendered the form below the footer), pull it back into our target so the form
// stays inside RouterView and is torn down with the route.
const reclaimBodyStrays = (el) => {
  document.body
    .querySelectorAll(':scope > .wFormContainer')
    .forEach((node) => el.appendChild(node))
}

// Block submit when a form's two email fields (Email + Re-enter Email) don't match,
// using the browser's own constraint validation — no library, no FormAssembly-side
// config. FA tags every email field with class `validate-email` (its own marker, stable
// across forms regardless of the numeric tfa_ id — the fields render as type="text", not
// type="email"), so a form carrying exactly two of them is Email (0) + Confirm (1). Inert
// on forms with a single email field (or none). Re-attaching a cached form brings its
// listeners along, so the dataset flag prevents double-wiring.
const wireEmailMatch = (el) => {
  const emails = el.querySelectorAll('input.validate-email')
  if (emails.length !== 2) return
  const [primary, confirm] = emails
  if (confirm.dataset.matchWired) return
  confirm.dataset.matchWired = 'true'

  const check = () => {
    confirm.setCustomValidity(
      confirm.value && confirm.value !== primary.value ? 'Email addresses must match' : ''
    )
  }
  primary.addEventListener('input', check)
  confirm.addEventListener('input', check)
  check()

  // Backstop: FormAssembly's wForms handles submit itself and can bypass native
  // constraint validation, so guard the injected <form> directly — re-check on submit
  // and block + surface the message if the emails still differ.
  confirm.form?.addEventListener(
    'submit',
    (e) => {
      check()
      if (!confirm.checkValidity()) {
        e.preventDefault()
        e.stopImmediatePropagation()
        confirm.reportValidity()
      }
    },
    true
  )
}

onMounted(() => {
  // Client-only: onMounted never runs during vite-ssg prerender, so the third-party form
  // is injected purely in the browser (the prerendered HTML ships only the empty target
  // + <noscript> fallback).
  const el = target.value

  // Revisiting a page whose form we already rendered this SPA session: re-attach the
  // fragment we detached on unmount — instant, and its event listeners/validation come
  // along with the nodes.
  const cached = formCache.get(props.formId)
  if (cached) {
    el.appendChild(cached)
    wireEmailMatch(el)
    return
  }

  const render = () => {
    // The loader binds its renderer to window's 'DOMContentLoaded', which fired long
    // before this lazy-loaded route mounted — so invoke its global entry point directly.
    // Called from the fresh script's onload, when our target div is guaranteed present,
    // so FormAssembly injects into `targetId` rather than falling back to <body>.
    window.loadFormAssemblyFormHeadAndBodyContents?.()
    reclaimBodyStrays(el)
    wireEmailMatch(el)
  }

  // Snapshot the FA theme stylesheets already in <head> so unmount can remove only the one
  // this form's loader is about to add (see THEME_LINK above).
  document.head.querySelectorAll(THEME_LINK).forEach((link) => preexistingThemes.add(link))

  // Drop any loader script a previous mount left in <body> before injecting a fresh one.
  // A stale body-level script is never a reliable "already loaded" signal — trusting it
  // is exactly what let the form re-render below the footer on later routes.
  document.getElementById(scriptId)?.remove()

  const script = document.createElement('script')
  script.id = scriptId
  // FormAssembly's inline "publish" loader — no document.write (SPA-safe). It injects the
  // form's real HTML into the element named by data-qp-target-id.
  script.src = `${props.baseUrl}/publish/${props.formId}`
  script.setAttribute('data-qp-target-id', targetId)
  script.onload = render
  document.body.appendChild(script)
})

onBeforeUnmount(() => {
  // Full teardown so nothing survives the route change as a <body> sibling.
  // 1) Detach (don't destroy) the rendered form so the next visit can re-attach it
  //    without re-running the loader. Vue is about to remove the target div; move its
  //    children out first to keep them (and their listeners) alive in the module cache.
  const el = target.value
  if (el && el.firstChild) {
    const frag = document.createDocumentFragment()
    while (el.firstChild) frag.appendChild(el.firstChild)
    formCache.set(props.formId, frag)
  }

  // 2) Remove the loader <script> from <body> — the leak's root cause: it outlived the
  //    component and made every later route treat the form as already loaded.
  document.getElementById(scriptId)?.remove()

  // 3) Drop any wrapper FormAssembly left as a direct <body> child (below the footer).
  document.body
    .querySelectorAll(':scope > .wFormContainer')
    .forEach((node) => node.remove())

  // 4) Remove the theme stylesheet this form's loader added to <head>, so the next route's
  //    form isn't styled by a theme belonging to a form that is no longer on the page. The
  //    cached-fragment path above deliberately does not put it back: the site styles the form
  //    completely (fa-form-fields), so a re-attached form renders on our tokens alone.
  dropInjectedThemes()
})
</script>

<template>
  <div class="fa-inline">
    <div :id="targetId" ref="target"></div>
    <noscript>
      <a :href="`${baseUrl}/${formId}`" target="_blank" rel="noopener">Complete this form</a>
    </noscript>
  </div>
</template>

<style lang="scss" scoped>
.fa-inline { width: 100%; }
</style>
